import { InputGroup } from '@chakra-ui/react'
import { Flex, Input } from '@chakra-ui/react'
import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { SearchValue } from '~/types/Shared'
import { Size } from '~/types/ThemeTypes'
import { tryGetValueOrDefault } from '~/utils/common'

import { useSearchInputString } from './atoms/SearchInput.atom'
import {
  PhoneSearchInputVariants,
  usePhoneSearchInputStyles,
} from './componentStyles/PhoneSearchInput.style'

interface SearchInputProps {
  searchInputValue?: string
  onSearchChange?: (newSearchString: SearchValue) => void
  disabled?: boolean
  inputRef?: React.RefObject<HTMLInputElement | null>
}
export const PhoneSearchInput = ({
  searchInputValue: externalInputValue,
  onSearchChange,
  disabled,
  inputRef: externalInputRef,
}: SearchInputProps) => {
  const { t } = useTranslation()
  const { searchValue, setInputString, getFormattedInput } =
    useSearchInputString()
  const internalInputRef = useRef<HTMLInputElement>(null)
  const inputRef = externalInputRef ?? internalInputRef

  const { label: inputString } = searchValue

  const derivedInputValue = tryGetValueOrDefault(
    externalInputValue,
    inputString,
  )

  const handleSearchChange = (newSearchString: string) => {
    //if externally controlled, don't set the internal input value, just hand back the formatting change
    if (externalInputValue !== undefined) {
      const { clean, formatted: derived } = getFormattedInput(newSearchString)
      onSearchChange?.(SearchValue.fromValueAndLabel(clean, derived))
      return
    }

    const searchValue = setInputString(newSearchString)
    onSearchChange?.(searchValue)
  }

  const handleClearSearch = () => {
    handleSearchChange('')
  }

  const variant = disabled
    ? PhoneSearchInputVariants.Disabled
    : derivedInputValue
      ? PhoneSearchInputVariants.Filled
      : PhoneSearchInputVariants.Ready

  const styles = usePhoneSearchInputStyles({ variant })

  // Extract layout properties to apply as native props (Chakra v3 requirement)
  const containerProps = {
    gap: styles.container?.gap,
  }

  const inputProps = {
    display: styles.input?.display,
    flex: styles.input?.flex,
  }

  return (
    <Flex css={styles.container} {...containerProps}>
      <InputGroup
        endElement={
          <MangoButton
            onClick={handleClearSearch}
            variant={MangoButtonVariant.Default_100}
            size={Size.sm}
            css={styles.inputRightElement}
          >
            {t('phoneSearchInput.clearButton')}
          </MangoButton>
        }
      >
        <Input
          ref={inputRef}
          disabled={disabled}
          css={styles.input}
          variant="flushed"
          {...inputProps}
          aria-label={t('phoneSearchInput.inputLabel')}
          placeholder={t('phoneSearchInput.placeholder')}
          value={derivedInputValue}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </InputGroup>
    </Flex>
  )
}
